import { assertNonNullish } from '@utils/assert';
import { action, computed, makeObservable, observable } from 'mobx';
import React from 'react';
import type { ModalConfig, ModalStepList, ModalStepTree, TreeNode, TreePath } from './types';

export class MultiStepModalStore {
  @observable public activeStepKey: string;

  @observable public isLoading = false;

  @observable public isDisabled = false;

  @action.bound
  setDisabled(disabled: boolean) {
    this.isDisabled = disabled;
  }

  @action.bound
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  private stepsTree = new Map<string, TreeNode>();

  private setTreeMap(step: ModalStepTree, parentKey?: string) {
    this.stepsTree.set(step.key, { ...step, parent: parentKey });

    if (step.children) {
      step.children.forEach((child) => this.setTreeMap(child, step.key));
    }
  }

  constructor(
    steps: ModalStepTree | ModalStepList,
    readonly config: ModalConfig,
  ) {
    const allSteps = Array.isArray(steps)
      ? (steps.reduceRight(
          (acc, step) => ({ ...step, children: acc ? [acc] : [] }),
          null as ModalStepTree | null,
        ) as ModalStepTree)
      : steps;

    this.setTreeMap(allSteps);

    this.activeStepKey = config.initialStep
      ? this.stepsTree.get(config.initialStep)?.key!
      : allSteps.key;

    if (!this.activeStepKey) {
      throw new Error('Invalid initial step');
    }

    makeObservable(this);
  }

  get currentStep() {
    return this.stepsTree.get(this.activeStepKey);
  }

  @computed get path() {
    const path: TreePath = [];
    let pathKey: string | undefined = this.activeStepKey;

    while (pathKey) {
      const currentStep = this.stepsTree.get(pathKey);

      assertNonNullish(currentStep, 'currentStep cannot be null');

      const { children, component, ...rest } = currentStep;
      const isActive = currentStep.key === this.activeStepKey;

      path.unshift({ ...rest, isPrevious: !isActive, isActive, isNext: false });

      pathKey = currentStep.parent;
    }

    return path;
  }

  public getFullPath(nextKey?: string): TreePath {
    const path = [...this.path];
    const nextNode =
      (nextKey && this.currentStep?.children?.find((s) => s.key === nextKey)) || undefined;

    if (nextNode) {
      path.push({ ...nextNode, isActive: false, isNext: true, isPrevious: false });
    }

    return [...path, ...this.getNextPaths(nextNode || this.currentStep)];
  }

  private getNextPaths(startPoint: TreeNode | undefined): TreePath {
    if (startPoint?.children?.length !== 1) {
      return [];
    }
    const nextStep = startPoint.children[0];
    const { component, children, ...nextNode } = nextStep;

    return [
      { ...nextNode, isActive: false, isNext: true, isPrevious: false },
      ...this.getNextPaths(nextStep),
    ];
  }

  private nextCallback?: (nextStepKey?: string) => void | Promise<void>;

  setNextCallback(callback?: (nextStepKey?: string) => void | Promise<void>) {
    this.nextCallback = callback;
  }

  @action.bound
  public async next(nextKey?: string): Promise<void> {
    if (this.isLoading || this.isDisabled) {
      return;
    }
    const nextStepKey = nextKey
      ? this.currentStep?.children?.find((s) => s.key === nextKey)?.key
      : this.currentStep?.children?.[0]?.key;

    if (nextKey && !nextStepKey) {
      throw new Error(`Next step with key "${nextKey}" not found in children.`);
    }

    try {
      const nextResult = this.nextCallback?.(nextStepKey);

      if (nextResult instanceof Promise) {
        this.setLoading(true);
        await nextResult;
        this.setLoading(false);
      }

      if (this.hasNext) {
        this.navigate(nextStepKey!);
      } else {
        this.dismiss();
      }
    } catch (error) {
    } finally {
      this.setLoading(false);
    }
  }

  public dismiss() {
    this.config.onClose();
  }

  @action.bound
  public prev() {
    if (this.hasPrev && !this.isLoading) {
      this.activeStepKey = this.currentStep?.parent!;
    }
  }

  @action.bound
  public navigate(pathKey: string) {
    if (this.isLoading || this.isDisabled) {
      return;
    }
    const node = this.stepsTree.get(pathKey);
    if (!node) {
      throw new Error(`Invalid step key: ${pathKey}`);
    }
    this.activeStepKey = pathKey;
  }

  get hasPrev() {
    return !!this.currentStep?.parent;
  }

  get hasNext() {
    return !!this.currentStep?.children?.length;
  }

  get isLinear() {
    return (this.currentStep?.children?.length || 0) <= 1;
  }
}

export const MultiStepModalCtx = React.createContext<MultiStepModalStore | undefined>(undefined);

export const useMultiStepModalStore = () =>
  React.useContext(MultiStepModalCtx) as MultiStepModalStore;
